import type { ButtonHTMLAttributes } from 'react'
import React from 'react'
import classNames from 'classnames'

import type { Icon } from '@slite/icons'
import { CheckmarkIcon } from '@slite/icons'

import type { BaseMenuItemProps } from './BaseMenuItem'
import { BaseMenuItem, BaseMenuItemContent } from './BaseMenuItem'
import {
  baseMenuItemClassnameFromSizeHorizontal,
  baseMenuItemClassnameFromSizeVertical,
} from './constants'
import { getMenuItemColorClasses } from './getMenuItemColorClasses'
import { useMenuContext } from './MenuProvider'
import { useArrowNavigation } from './useArrowNavigation'

export type MenuItemProps = ButtonHTMLAttributes<HTMLButtonElement> &
  Omit<BaseMenuItemProps, 'children' | 'onClick' | 'id'> & {
    id: string
    icon?: Icon | React.ReactNode
    iconClassName?: string

    isActive?: boolean
    isSelected?: boolean
    isChecked?: boolean

    'data-test-id'?: string
    onClick?: (id: string) => void
    ignoreRowClickHandler?: boolean

    preventDefaultOnClick?: boolean
    stopPropagationOnClick?: boolean
    ref?: React.Ref<HTMLButtonElement>
  }

export function MenuItem({
  id,
  icon,
  iconClassName,
  children,
  onClick,
  onKeyDown,
  ignoreRowClickHandler = false,
  'data-test-id': dataTestId = 'menuItem',
  tabIndex,
  style,
  className,
  autoFocus,
  preventDefaultOnClick = false,
  stopPropagationOnClick = false,
  alignItems,
  disabled,
  isActive,
  isSelected,
  isChecked,
  tooltip,
  tooltipPositions,
  ref,
  ...props
}: MenuItemProps) {
  const { onRowClick: onMenuRowClick, size } = useMenuContext()
  const _onKeyDown = useArrowNavigation(onKeyDown)

  const handleClick = (event: React.MouseEvent) => {
    if (preventDefaultOnClick) {
      event.preventDefault()
    }

    if (stopPropagationOnClick) {
      event.stopPropagation()
    }

    if (onClick) {
      onClick(id)
    }

    if (onMenuRowClick && !ignoreRowClickHandler) {
      onMenuRowClick(id)
    }
  }

  return (
    <BaseMenuItem
      tooltip={tooltip}
      tooltipPositions={tooltipPositions}
      alignItems={alignItems}
    >
      {(_className) => (
        <button
          {...props}
          ref={ref}
          type="button"
          className={classNames(
            _className,

            getMenuItemColorClasses({
              isActive,
              isSelected,
              isDisabled: disabled,
            }),

            {
              [baseMenuItemClassnameFromSizeHorizontal[size]]:
                !className?.includes('padding-horizontal'),
              [baseMenuItemClassnameFromSizeVertical[size]]:
                !className?.includes('padding-vertical'),
            },
            className
          )}
          onClick={handleClick}
          tabIndex={tabIndex ?? 0}
          data-test-id={dataTestId}
          style={style}
          onKeyDown={_onKeyDown}
          autoFocus={autoFocus}
          disabled={disabled}
        >
          <BaseMenuItemContent
            icon={icon}
            iconClassName={classNames(disabled && 'shade-30', iconClassName)}
            isDisabled={disabled}
            isSelected={isSelected}
          >
            <div className="flexbox flexbox-align-center flexbox-grow text-align-left max-width-full">
              {children}
            </div>
            {isChecked && (
              <CheckmarkIcon className="flexbox-no-shrink margin-top-2" />
            )}
          </BaseMenuItemContent>
        </button>
      )}
    </BaseMenuItem>
  )
}
